import { Bell, User, Settings, LogOut, Languages, Clock, Banknote } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import {
  countryCodeToFlag,
  formatCountryName,
  formatTenantClock,
  UI_TIMING,
  type SupportedPortalLocale,
  type TenantTimeFormat,
} from '@heglo/ui/tokens';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuthStore } from '@/store/authStore';
import { useShopStaffAccess } from '@/hooks/useShopStaffAccess';
import { useUIStore } from '@/store/uiStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useBrandingTheme } from '@/hooks/useBrandingTheme';
import { TenantSwitcher } from './TenantSwitcher';

function useTenantClock(
  timezone: string | null,
  locale: SupportedPortalLocale,
  timeFormat: TenantTimeFormat,
) {
  const [time, setTime] = useState('');

  useEffect(() => {
    if (!timezone) {
      setTime('');
      return;
    }
    const update = () => setTime(formatTenantClock(new Date(), locale, timezone, timeFormat));
    update();
    const interval = window.setInterval(update, UI_TIMING.tenantClockRefreshMs);
    return () => window.clearInterval(interval);
  }, [locale, timeFormat, timezone]);

  return time;
}

export function Header() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    user,
    role,
    logout,
    tenantCurrency,
    tenantTimezone,
    tenantCountry,
    tenantCountryCode,
    tenantTimeFormat,
  } = useAuthStore();
  const { language, setLanguage, enabledLanguages } = useUIStore();
  const { isSuperAdmin } = useTenantContext();
  const isProviderAdmin = role === 'provideradmin' || role === 'providerstaff';
  const staff = useShopStaffAccess();
  const showOwnerSettings = role !== 'providerstaff' && (!isProviderAdmin || (staff.isFetched && !staff.isError && !staff.isStaff));
  const settingsPath = isSuperAdmin ? '/platform-settings' : isProviderAdmin ? '/business/settings' : '/settings';
  const { appName, appNameArabic } = useBrandingTheme();
  const currentTime = useTenantClock(tenantTimezone, language, tenantTimeFormat);
  const nextLanguage = enabledLanguages.find((item) => item !== language);
  const isArabic = language === 'ar';

  const countryLabel = useMemo(
    () => tenantCountryCode
      ? formatCountryName(tenantCountryCode, language, tenantCountry || '')
      : tenantCountry || '',
    [language, tenantCountry, tenantCountryCode],
  );
  const countryFlag = tenantCountryCode ? countryCodeToFlag(tenantCountryCode) : '';

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const getInitials = (name?: string) => {
    const resolvedName = name?.trim() || t('layout.header.user');
    return resolvedName
      .split(/\s+/)
      .map((part) => part[0])
      .join('')
      .slice(0, 2);
  };

  return (
    <header className="h-[var(--admin-header-height)] bg-background border-b px-[var(--admin-content-padding)] flex items-center justify-between sticky top-0 z-30">
      {!isSuperAdmin ? (
        <div className="hidden md:flex items-center gap-4 min-w-0">
          <span className="truncate text-sm font-semibold text-foreground">
            {(isArabic ? appNameArabic : appName) || appName || appNameArabic || ''}
          </span>

          {(countryLabel || tenantCurrency || currentTime) && <div className="h-5 w-px bg-border" />}

          {countryLabel && (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              {countryFlag && <span className="text-base" aria-hidden="true">{countryFlag}</span>}
              <span>{countryLabel}</span>
            </div>
          )}

          {tenantCurrency && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Banknote className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-medium">{tenantCurrency}</span>
            </div>
          )}

          {currentTime && (
            <div
              className="flex items-center gap-1 text-sm text-muted-foreground"
              aria-label={t('layout.header.localTime', { time: currentTime })}
            >
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="font-mono tabular-nums">{currentTime}</span>
            </div>
          )}
        </div>
      ) : (
        <div className="hidden md:flex items-center gap-3">
          <span className="text-sm font-medium text-muted-foreground">
            {t('layout.header.platformAdmin')}
          </span>
          <TenantSwitcher />
        </div>
      )}

      <div className="ms-auto flex items-center gap-2 sm:gap-4">
        {!isSuperAdmin && !isProviderAdmin && (
          <Button
            variant="ghost"
            size="icon"
            className="relative text-muted-foreground hover:text-primary"
            onClick={() => navigate('/notifications')}
            aria-label={t('layout.header.notifications')}
            title={t('layout.header.notifications')}
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            <span className="absolute top-2 end-2 h-2 w-2 bg-danger rounded-full border-2 border-background" aria-hidden="true" />
          </Button>
        )}

        {nextLanguage && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-muted-foreground hover:text-primary border-muted-foreground/30"
            onClick={() => setLanguage(nextLanguage)}
            aria-label={t('layout.header.switchLanguage', {
              language: t(`common.languages.${nextLanguage}`),
            })}
          >
            <Languages className="h-4 w-4" aria-hidden="true" />
            <span className="font-medium">{t(`common.languageShort.${nextLanguage}`)}</span>
          </Button>
        )}

        <div className="flex items-center gap-3 sm:border-e sm:pe-4 sm:me-2">
          <div className="text-start hidden sm:block">
            <p className="text-sm font-semibold leading-none">
              {user?.fullName || t('layout.header.user')}
            </p>
            <p className="text-xs text-muted-foreground mt-1">{t(role === 'providerstaff' || staff.isStaff ? 'merchantStaff.teamMember' : isProviderAdmin ? 'layout.header.providerAdmin' : role === 'tenantadmin' ? 'layout.header.tenantAdmin' : 'layout.header.admin')}</p>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="relative h-10 w-10 rounded-full"
                aria-label={t('layout.header.profile')}
                title={t('layout.header.profile')}
              >
                <Avatar className="h-10 w-10 border border-border">
                  <AvatarImage src={user?.profileImage} alt={user?.fullName || ''} />
                  <AvatarFallback>{getInitials(user?.fullName)}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user?.fullName || t('layout.header.user')}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.mobileNo || user?.email || ''}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {showOwnerSettings && <>
              <DropdownMenuItem asChild>
                <Link to={settingsPath} className="flex items-center cursor-pointer">
                  <User className="me-2 h-4 w-4" aria-hidden="true" />
                  {t('layout.header.profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={settingsPath} className="flex items-center cursor-pointer">
                  <Settings className="me-2 h-4 w-4" aria-hidden="true" />
                  {t('layout.header.settings')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              </>}
              <DropdownMenuItem
                className="text-danger focus:text-danger cursor-pointer"
                onClick={handleLogout}
              >
                <LogOut className="me-2 h-4 w-4" aria-hidden="true" />
                {t('layout.header.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
